import React, {useCallback, useMemo} from 'react';
import type {QuestionModelV3} from '@albert-io/models';
import {Fieldset} from '@albert-io/atomic';
import classnames from 'classnames';
import {useShallow} from 'zustand/react/shallow';
import {useQuestionTranslationStore} from 'components/QuestionTypes/shared/TranslationSection';
import {ALTERNATE_CONTENT_LANGUAGES} from 'components/QuestionTypes/shared/TranslationSection/Translation.types';
import {MarkdownWithSupplement} from 'client/components/QuestionTypes/shared/MarkdownWithSupplement';

import Row from './TwoWayQuestionTableRow.react';

type TwoWayQuestionStore = any;

interface TwoWayQuestionTableProps {
  isGuessSubmitted: boolean;
  showResponseStats: boolean;
  store: TwoWayQuestionStore;
  question: QuestionModelV3;
}

const TwoWayQuestionTable = ({
  question,
  store,
  isGuessSubmitted = false,
  showResponseStats = false
}: TwoWayQuestionTableProps) => {
  // Translation info
  const {translatedQuestionInfo, showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      translatedQuestionInfo: state.translatedQuestionInfo,
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  const containsLatex = useCallback((questionArg: QuestionModelV3) => {
    const func = () => {
      const regex = /\$.+\$/;
      if (
        regex.test(questionArg.get('header_left')) ||
        regex.test(questionArg.get('header_right'))
      ) {
        return true;
      }
      return questionArg
        .getRows()
        .some(
          (row) =>
            regex.test(row.getLeftColumn().getText()) ||
            regex.test(row.getRightColumn().getText()) ||
            regex.test(row.getStatement())
        );
    };
    return questionArg.cache('containsLatex', func);
  }, []);

  const hasHeader = useMemo(() => {
    const headerLeft = question.get('header_left');
    const headerRight = question.get('header_right');
    const hasHeaderBool = !!(headerLeft || headerRight);
    return hasHeaderBool;
  }, [question]);

  const headerText = useMemo(() => {
    if (showTranslations && translatedQuestionInfo) {
      return {
        left: translatedQuestionInfo.header_left,
        right: translatedQuestionInfo.header_right
      };
    }
    return {
      left: question.get('header_left'),
      right: question.get('header_right')
    };
  }, [question, translatedQuestionInfo, showTranslations]);

  // Single source for both the rendered rows and the anchor lang, so they can't diverge.
  const translatedRows = showTranslations ? translatedQuestionInfo?.rows : null;

  const rows = useMemo(() => {
    if (translatedRows) {
      const formattedRows = translatedRows.map((row) => {
        const {columns = defaultColumn, id, statement} = row;
        const {left, right} = columns;

        const leftOption = {
          ...left,
          getText: () => left?.text,
          getId: () => left?.id
        };
        const rightOption = {
          ...right,
          getText: () => right?.text,
          getId: () => right?.id
        };
        return {
          getStatement: () => statement,
          getId: () => id,
          getLeftColumn: () => leftOption,
          getRightColumn: () => rightOption,
          ...row
        };
      });
      return formattedRows;
    }
    return question.getRows();
  }, [translatedRows, question]);

  const renderHeading = (text: string) => {
    return <MarkdownWithSupplement text={text} className='twq-table__heading' />;
  };

  return (
    <Fieldset>
      <table
        className={classnames('twq-table mx-x2! max-w-none!', {
          'twq-table--full-width': containsLatex(question)
        })}
      >
        {hasHeader && (
          <thead>
            <tr>
              <th className='twq-table__heading p-x2! leading-[1.2]' />
              <th className='p-x2! min-w-[16ch] leading-[1.2] whitespace-normal!'>
                {renderHeading(headerText.left)}
              </th>
              <th className='p-x2! min-w-[16ch] leading-[1.2] whitespace-normal!'>
                {renderHeading(headerText.right)}
              </th>
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <Row
              // oxlint-disable-next-line react/no-array-index-key -- two-way question rows: fixed set per question, never reordered
              key={i}
              row={row}
              store={store}
              question={question}
              isGuessSubmitted={isGuessSubmitted}
              showResponseStats={showResponseStats}
              lang={translatedRows ? ALTERNATE_CONTENT_LANGUAGES[0] : null}
            />
          ))}
        </tbody>
      </table>
    </Fieldset>
  );
};

export default TwoWayQuestionTable;

const defaultColumn = {
  right: {
    id: '',
    text: ''
  },
  left: {
    id: '',
    text: ''
  }
};
